// Kanal-Linien-Ansicht (Welle 1, #3 Vollbild + #8 Umschalter): eine Linie je Kanal, damit
// die relative Entwicklung der Plattformen zueinander sichtbar wird. SVG-Konventionen
// (viewBox, Padding, Tick-Ausduennung, Tooltip-Optik) identisch zu StackedBarChart.tsx.
import { useMemo, useState } from "react";
import { evenlySpacedIndices } from "@/lib/chart-math";
import { CHANNEL_COLORS } from "./StackedBarChart";
import type { DashSeriesBucket } from "@/api/dashboard";

const PADDING = { t: 14, r: 8, b: 26, l: 46 };
const VIEWBOX_WIDTH = 640;
const defaultFormatValue = (n: number) => n.toLocaleString("de-DE");

interface ChannelLineChartProps {
  buckets: DashSeriesBucket[];
  channels: string[];
  colors?: Record<string, string>;
  height?: number;
  formatValue?: (n: number) => string;
}

export function ChannelLineChart({
  buckets,
  channels,
  colors = CHANNEL_COLORS,
  height = 280,
  formatValue = defaultFormatValue,
}: ChannelLineChartProps) {
  const [hover, setHover] = useState(-1);

  // Codex-Finding B: Domain umfasst 0 UND alle Werte — negative Kanalwerte (Gutschriften)
  // wuerden bei einer [0, max]-Skala unterhalb des SVG geclippt. minV/maxV sind beide auf
  // 0 geankert, damit die Nulllinie immer im Bild liegt.
  const { points, minV, maxV } = useMemo(() => {
    let min = 0;
    let max = 0;
    const pts = channels.map((ch) =>
      buckets.map((b) => {
        const v = b.values[ch] ?? 0;
        if (v > max) max = v;
        if (v < min) min = v;
        return v;
      }),
    );
    return { points: pts, minV: min, maxV: max };
  }, [buckets, channels]);

  if (buckets.length === 0) {
    return (
      <div role="img" aria-label="Kanal-Linien-Diagramm, keine Daten." style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>Keine Daten für diesen Zeitraum.</span>
      </div>
    );
  }

  const innerW = VIEWBOX_WIDTH - PADDING.l - PADDING.r;
  const innerH = height - PADDING.t - PADDING.b;
  const x = (i: number) => PADDING.l + (buckets.length === 1 ? innerW / 2 : (i * innerW) / (buckets.length - 1));
  // Guard: bei komplett flacher Serie (alle Werte 0) waere die Spanne 0 → Division durch 0.
  const span = maxV - minV || 1;
  const y = (v: number) => PADDING.t + innerH - ((v - minV) / span) * innerH;
  const tickIndices = evenlySpacedIndices(buckets.length, 6);
  // Ticks ueber die Misch-Domain [minV, maxV]; Rundung kann bei kleinen Spannen gleiche
  // Werte erzeugen → Duplikate ausduennen. Key ist der Bruchteil-Index (nicht der Wert),
  // damit React-Keys auch bei kollidierenden Werten eindeutig bleiben.
  const yTicks = [0, 0.25, 0.5, 0.75, 1]
    .map((f, i) => ({ i, v: Math.round(minV + (maxV - minV) * f) }))
    .filter((t, idx, arr) => arr.findIndex((u) => u.v === t.v) === idx);
  const hoverBucket = hover >= 0 ? buckets[hover] : undefined;

  return (
    <div style={{ position: "relative" }}>
      <svg
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`Kanal-Linien-Diagramm mit ${buckets.length} Zeitabschnitten und ${channels.length} Kanälen (${channels.join(", ")}).`}
        onMouseLeave={() => setHover(-1)}
      >
        {yTicks.map(({ i, v }) => (
          <g key={i}>
            <line x1={PADDING.l} x2={VIEWBOX_WIDTH - PADDING.r} y1={y(v)} y2={y(v)} stroke="var(--line-2)" strokeWidth={1} strokeDasharray={v === 0 ? undefined : "2 3"} />
            <text x={PADDING.l - 8} y={y(v) + 3} fontSize="10" fill="var(--muted)" textAnchor="end" fontFamily="var(--m-font)">{formatValue(v)}</text>
          </g>
        ))}
        {minV < 0 && (
          // Nulllinie als eigener, durchgezogener Strich — bei negativen Werten liegt 0
          // mitten im Diagramm und braucht eine sichtbare Bezugslinie.
          <line data-testid="zero-baseline" x1={PADDING.l} x2={VIEWBOX_WIDTH - PADDING.r} y1={y(0)} y2={y(0)} stroke="var(--muted)" strokeWidth={1} />
        )}
        {points.map((series, ci) => (
          <polyline
            key={channels[ci]}
            fill="none"
            stroke={colors[channels[ci]!] ?? colors.Sonstige}
            strokeWidth={1.75}
            points={series.map((v, i) => `${x(i)},${y(v)}`).join(" ")}
          />
        ))}
        {buckets.map((_, i) => (
          <rect key={i} x={x(i) - innerW / (2 * Math.max(buckets.length - 1, 1))} y={PADDING.t} width={innerW / Math.max(buckets.length - 1, 1)} height={innerH} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
        {hover >= 0 && <line x1={x(hover)} x2={x(hover)} y1={PADDING.t} y2={PADDING.t + innerH} stroke="var(--muted)" strokeWidth={1} strokeDasharray="3 3" />}
        {tickIndices.map((i) => (
          <text key={i} x={x(i)} y={height - 8} fontSize="10" fill="var(--muted)" textAnchor="middle" fontFamily="var(--m-font)">{buckets[i]!.label}</text>
        ))}
      </svg>
      {hoverBucket && (
        <div className="absolute top-2 right-2 rounded bg-ink/95 text-white text-[10px] font-mono px-2.5 py-2 pointer-events-none space-y-0.5">
          <div className="font-bold">{hoverBucket.label}</div>
          <div className="font-bold">Gesamt: {formatValue(channels.reduce((s, ch) => s + (hoverBucket.values[ch] ?? 0), 0))}</div>
          {channels.map((ch) => (
            <div key={ch} className="flex items-center gap-1.5">
              <span className="inline-block size-1.5 rounded-sm" style={{ background: colors[ch] ?? colors.Sonstige }} />
              {ch}: {formatValue(hoverBucket.values[ch] ?? 0)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
